import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import searchServiceAPI from '../api/search.service.api';
import { assembleResultsFromApiGroups } from '../utils/search-v2-mapper';
import { SEARCH_V2_FETCH_LIMIT } from '../constants/search.constants';

// Kept out of search.hooks.js: widget-app imports that file and cannot bundle react-query.

type StoreState = Record<string, unknown>;

export const searchKeys = {
  all: ['search'] as const,
  term: (term: string) => [...searchKeys.all, 'term', term] as const,
};

/**
 * Search v2 for a submitted term, no type filter: the search page's hits. Idle
 * while the term is empty.
 *
 * @param term - The term the user submitted.
 * @returns The query; `data` as `assembleResultsFromApiGroups` builds it.
 * @example
 * const { data: results, isFetching } = useSearch(submittedTerm);
 * const hits = getPickerHits(results);
 */
export function useSearch(term: string) {
  const query = term.trim();
  const knowledge = useSelector((state: StoreState) => state.knowledge);
  const lab = useSelector((state: StoreState) => state.lab);
  const admission = useSelector((state: StoreState) => state.admission);
  const contacts = useSelector((state: StoreState) => state.contacts);
  const usersManagement = useSelector((state: StoreState) => state.usersManagement);

  const result = useQuery({
    queryKey: searchKeys.term(query),
    queryFn: async () => {
      const response = await searchServiceAPI.search({
        query,
        groupBy: 'workspace',
        limit: SEARCH_V2_FETCH_LIMIT,
      });
      if (!response?.success) throw response?.error ?? new Error('Search failed');
      return response.data;
    },
    enabled: Boolean(query),
  });

  const data = useMemo(
    () =>
      result.data &&
      assembleResultsFromApiGroups({
        groups: result.data.groups,
        facets: result.data.facets,
        count: result.data.count,
        state: { knowledge, lab, admission, contacts, usersManagement },
      }),
    [result.data, knowledge, lab, admission, contacts, usersManagement],
  );

  return { ...result, data };
}
